/**
 * Hook for reading the shared roster players for squad planning.
 *
 * Thin context read. The actual fetch (getSquadPlayersByPosition) and the
 * firstTeam/academy/targets transform live in SquadPlanningPlayersProvider
 * (contexts/SquadPlanningPlayersContext), mounted once in
 * SquadScreenContainer above every one of the roughly 12 organisms that call
 * this hook.
 *
 * Before SE-8191's follow-up fix, this hook called
 * useSquadPlanningGetSquadPlayersByPositionQuery directly, so every call
 * site ran its own independent Apollo ObservableQuery / cache watcher over
 * the same query and variables. On a scenario switch, all of those watchers
 * re-diffed the cache and re-ran their own firstTeam/academy/targets
 * transform synchronously in the same tick, which was measured to block the
 * main thread for seconds and trip Chrome Page Unresponsive (QA kickback,
 * SE-8191, retested 2026-09-13). See SquadPlanningPlayersContext module doc
 * for the full story; this file keeps the same name and return shape so no
 * call site needed to change.
 * @module features/squad-planning/hooks/useSquadPlanningPlayers
 */

import { useSquadPlanningPlayersContext } from "../contexts/SquadPlanningPlayersContext";
import type { SquadPlanningPlayersContextValue } from "../contexts/SquadPlanningPlayersContext";

/** Return type for {@link useSquadPlanningPlayers}. */
export type UseSquadPlanningPlayersResult = SquadPlanningPlayersContextValue;

/**
 * Reads the squad-planning player list, loading state, error, and refetch
 * function from the shared SquadPlanningPlayersProvider.
 * @returns Players, loading state, error, and refetch function
 */
export function useSquadPlanningPlayers(): UseSquadPlanningPlayersResult {
  return useSquadPlanningPlayersContext();
}
